import React, { useState } from 'react';
import { Stack, TextField, Typography, Box } from '@mui/material';
import { reportApi } from '../../../services/reportService';
import { StatTile, StatusPill } from '../../../components/ui';
import { tokens } from '../../../theme';
import { countLabel, formatDate, formatDayMonth } from '../../../utils/format';
import {
  SectionCard,
  CHART_COLORS,
  formatPLN,
  formatNumber,
  useAsyncData,
  StatsLoader,
  EmptyHint,
  StatTileGrid,
  MetricToggle,
} from './statsShared';
import { GroupedBarChart } from './ChartKit';

interface Props {
  salonId: string;
}

const today = () => new Date().toISOString().split('T')[0];

export const WeekTab: React.FC<Props> = ({ salonId }) => {
  const [weekOf, setWeekOf] = useState(today());
  const [metric, setMetric] = useState<'revenue' | 'bookings'>('revenue');
  const { data, loading, error } = useAsyncData(() => reportApi.getWeekStats(salonId, weekOf), [salonId, weekOf]);

  const totalBookings = (data?.days ?? []).reduce((s, d) => s + d.bookings, 0);
  const hasActivity = !!data && data.days.some((d) => d.revenue > 0 || d.bookings > 0);

  return (
    <Stack gap={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-end' }} gap={{ xs: 1, sm: 2 }} sx={{ mb: 1 }}>
        <TextField
          type="date"
          size="small"
          label="Dowolny dzień tygodnia"
          value={weekOf}
          onChange={(e) => setWeekOf(e.target.value)}
          inputProps={{ max: today() }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 210 } }}
        />
        {data && (
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: tokens.inkSoft, pb: { sm: 1 }, fontVariantNumeric: 'tabular-nums' }}>
            Tydzień {formatDate(data.weekStart)} – {formatDate(data.weekEnd)}
          </Typography>
        )}
      </Stack>

      <StatsLoader loading={loading} error={error} hasData={!!data}>
        {data && (
          <Stack gap={2}>
            <StatTileGrid>
              <StatTile label="Przychód tygodnia" value={formatPLN(data.total)} change={Math.round(data.change)} changeLabel="niż tydzień wcześniej" />
              <StatTile label="Wizyty" value={formatNumber(totalBookings)} />
              <StatTile
                label="Najlepszy dzień"
                value={data.best ? formatPLN(data.best.revenue) : '—'}
                hint={data.best ? `${data.best.dayLabel}, ${formatDayMonth(data.best.date)}` : undefined}
              />
              <StatTile
                label="Najsłabszy dzień"
                value={data.worst ? formatPLN(data.worst.revenue) : '—'}
                hint={data.worst ? `${data.worst.dayLabel}, ${formatDayMonth(data.worst.date)}` : undefined}
              />
            </StatTileGrid>

            <SectionCard
              title="Przychód i wizyty według dnia"
              subtitle={metric === 'revenue' ? 'Przychód w kolejnych dniach tygodnia' : 'Liczba wizyt w kolejnych dniach tygodnia'}
              action={hasActivity ? <MetricToggle value={metric} onChange={setMetric} /> : undefined}
            >
              {hasActivity ? (
                <GroupedBarChart
                  data={data.days.map((d) => ({ label: d.dayShort, revenue: d.revenue, bookings: d.bookings })) as unknown as Record<string, unknown>[]}
                  xKey="label"
                  bars={
                    metric === 'revenue'
                      ? [{ key: 'revenue', name: 'Przychód', color: CHART_COLORS[0], currency: true }]
                      : [{ key: 'bookings', name: 'Wizyty', color: CHART_COLORS[0] }]
                  }
                  height={300}
                />
              ) : (
                <EmptyHint description="Wybierz dzień z innego tygodnia.">Brak wizyt i sprzedaży w tym tygodniu</EmptyHint>
              )}
            </SectionCard>

            <SectionCard title="Podsumowanie dni">
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
                }}
              >
                {data.days.map((d) => {
                  const isBest = !!data.best && d.date === data.best.date;
                  return (
                    <Box
                      key={d.date}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        border: `1px solid ${isBest ? tokens.plumLight : tokens.line}`,
                        bgcolor: isBest ? tokens.plumSofter : tokens.surface,
                        minWidth: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.inkSoft }}>{d.dayLabel}</Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {formatDayMonth(d.date)}
                      </Typography>
                      <Typography sx={{ fontSize: '1.0625rem', fontWeight: 750, mt: 0.75, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatPLN(d.revenue)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: tokens.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                        {countLabel(d.bookings, 'wizyta', 'wizyty', 'wizyt')}
                      </Typography>
                      {isBest && <StatusPill label="Najlepszy dzień" tone="plum" size="small" noDot sx={{ mt: 1 }} />}
                    </Box>
                  );
                })}
              </Box>
            </SectionCard>
          </Stack>
        )}
      </StatsLoader>
    </Stack>
  );
};
